import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";

import { useError } from "../../../contexts/ErrorContext";
import { useConnectorUsageStats } from "../../../hooks/useFulfillmentsAnalytics";

import {
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface FulfillmentsConnectorCoverageChartProps {
  dateStart?: string;
  dateEnd?: string;
}

const FulfillmentsConnectorCoverageChart: React.FC<
  FulfillmentsConnectorCoverageChartProps
> = ({ dateStart = undefined, dateEnd = undefined }) => {
  const { showError } = useError();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [chartView, setChartView] = useState<string | null>("fulfillments");

  const handleChartViewToggle = (
    event: React.MouseEvent<HTMLElement>,
    newView: "customers" | "fulfillments" | null
  ) => {
    if (newView !== null) {
      setChartView(newView);
    }
  };

  const {
    data: connectorCoverageData,
    error,
    isLoading,
  } = useConnectorUsageStats(dateStart, dateEnd);

  useEffect(() => {
    if (error) {
      showError(error?.message || `Failed to get Connector usage data`);
    }
  }, [error, showError]);

  const yData =
    chartView === "customers"
      ? {
          count: connectorCoverageData?.map((item) => item.count_customers),
          connectorCount: connectorCoverageData?.map(
            (item) => item.count_connector_customers
          ),
          pct: connectorCoverageData?.map(
            (item) => item.pct_connector_customers
          ),
          title: "Customers",
        }
      : {
          count: connectorCoverageData?.map((item) => item.count_fulfillments),
          connectorCount: connectorCoverageData?.map(
            (item) => item.count_connector_fulfillments
          ),
          pct: connectorCoverageData?.map(
            (item) => item.pct_connector_fulfillments
          ),
          title: "Fulfillments",
        };
  const yLabel = chartView === "customers" ? "Customers" : "Fulfillments";

  const nonConnectorCount =
    yData.count?.map(
      (total, index) => total - (yData.connectorCount?.[index] || 0)
    ) || [];

  return (
    <Box sx={{ display: "block", width: "100%", minHeight: "500px" }}>
      {isLoading ? (
        <CircularProgress
          size={20}
          sx={{ marginRight: 1, verticalAlign: "middle" }}
        />
      ) : connectorCoverageData && connectorCoverageData.length > 0 ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mb: 2,
            }}
          >
            <ToggleButtonGroup
              color="primary"
              value={chartView}
              exclusive
              onChange={handleChartViewToggle}
              aria-label="connector-usage-chart-view"
            >
              <ToggleButton
                value="customers"
                aria-label="customers"
                style={{ width: "200px" }}
              >
                <Typography variant="button">Customers</Typography>
              </ToggleButton>
              <ToggleButton
                value="fulfillments"
                aria-label="fulfillments"
                style={{ width: "200px" }}
              >
                <Typography variant="button">Fulfillments</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Plot
            config={{
              displaylogo: false,
              modeBarButtonsToRemove: ["select2d", "lasso2d"],
              responsive: true,
            }}
            data={[
              {
                x: connectorCoverageData?.map((item) => {
                  const date = new Date(item.week_start_date);
                  return date.toISOString().split("T")[0];
                }),
                y: yData.connectorCount,
                type: "bar",
                name: `Connector ${yLabel}`,
                hovertemplate: `<b>Connector ${yLabel}:</b> %{y:,}<extra></extra>`,
                customdata: yData.pct,
                marker: { color: "#2A9D8F" },
              },
              {
                x: connectorCoverageData?.map((item) => {
                  const date = new Date(item.week_start_date);
                  return date.toISOString().split("T")[0];
                }),
                y: nonConnectorCount,
                type: "bar",
                name: `Manual ${yLabel}`,
                hovertemplate: `<b>Manual ${yLabel}:</b> %{y:,}<extra></extra>`,
                marker: { color: "#E9C46A" },
              },
              {
                x: connectorCoverageData?.map((item) => {
                  const date = new Date(item.week_start_date);
                  return date.toISOString().split("T")[0];
                }),
                y: yData.pct,
                type: "scatter",
                mode: "lines+markers",
                name: "Connector Coverage (%)",
                yaxis: "y2",
                hovertemplate: `<b>Connector Coverage:</b> %{y:.2f}%<extra></extra>`,
                marker: { color: "#E76F51", size: 4 },
                line: { color: "#E76F51", width: 2 },
              },
            ]}
            layout={{
              title: {
                text: `Connector Coverage (${yLabel})`,
                font: {
                  color: primaryColor,
                },
              },
              xaxis: {
                title: {
                  text: "Week Start Date",
                  standoff: 15,
                },
                showgrid: false,
                tickangle: -45,
                tickfont: {
                  size: 12,
                },
              },
              yaxis: {
                title: yData.title,
                showgrid: true,
                gridcolor: "#D3D3D3",
                zeroline: true,
              },
              yaxis2: {
                title: "Connector Coverage (%)",
                overlaying: "y",
                side: "right",
                showgrid: false,
                zeroline: true,
                range: [0, 105],
              },
              barmode: "stack",
              hovermode: "x unified",
              margin: {
                b: 100,
                t: 100,
              },
              hoverlabel: {
                bgcolor: "#FFF",
                font: { color: "#264653" },
                bordercolor: "#E76F51",
                align: "left",
              },
              font: {
                family: '"Noto Sans", serif',
                size: 14,
              },
              legend: {
                orientation: "h",
                x: 0.5,
                y: 1.2,
                xanchor: "center",
                yanchor: "top",
              },
            }}
            style={{ width: "100%", height: "100%" }}
          />
        </>
      ) : (
        <p>No data available</p>
      )}
    </Box>
  );
};

export default FulfillmentsConnectorCoverageChart;
